import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminProfits.css';
import { API_URL } from '../config/data';

const AdminProfits = () => {
  const navigate = useNavigate();
  const [profits, setProfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProfit, setEditingProfit] = useState(null);
  const [users, setUsers] = useState([]);
  const [userAccounts, setUserAccounts] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    userId: '',
    account_number: '',
    from_date: '',
    to_date: '',
    amount: '',
    percentage: '',
    admin_comment: ''
  });
  const [editFormData, setEditFormData] = useState({
    userId: '',
    account_number: '',
    from_date: '',
    to_date: '',
    amount: '',
    percentage: '',
    admin_comment: ''
  });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchProfits();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate]);

  const fetchProfits = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axios.get(`${API_URL}/api/admin/profits?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProfits(response.data.profits || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching profits:', error);
      setError('Ошибка загрузки доходов');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProfits(1);
  };

  const handleEditProfit = (profit) => {
    setEditingProfit(profit);
    // Преобразуем дату из формата DD.MM.YYYY в формат YYYY-MM-DD для input type="date"
    const convertDateToInputFormat = (dateString) => {
      if (!dateString) return '';
      if (typeof dateString === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('.');
        return `${year}-${month}-${day}`;
      }
      return dateString;
    };

    setEditFormData({
      userId: profit.userId || '',
      account_number: profit.account_number || '',
      from_date: convertDateToInputFormat(profit.from_date),
      to_date: convertDateToInputFormat(profit.to_date),
      amount: profit.amount || '',
      percentage: profit.percentage || '',
      admin_comment: profit.admin_comment || ''
    });
    fetchUsers();
    if (profit.userId) {
      fetchUserAccounts(profit.userId);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'userId') {
      setEditFormData(prev => ({
        ...prev,
        [name]: value,
        account_number: ''
      }));
      fetchUserAccounts(value);
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const profitData = {
        userId: parseInt(editFormData.userId),
        account_number: editFormData.account_number,
        from_date: editFormData.from_date,
        to_date: editFormData.to_date,
        amount: parseFloat(editFormData.amount),
        percentage: parseFloat(editFormData.percentage),
        admin_comment: editFormData.admin_comment
      };

      await axios.put(`${API_URL}/api/admin/profits/${editingProfit.id}`, profitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEditingProfit(null);
      setEditFormData({
        userId: '',
        account_number: '',
        from_date: '',
        to_date: '',
        amount: '',
        percentage: '',
        admin_comment: ''
      });
      setUserAccounts([]);
      await fetchProfits(currentPage);
    } catch (error) {
      console.error('Error updating profit:', error);
      setError('Ошибка обновления дохода: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingProfit(null);
    setEditFormData({
      userId: '',
      account_number: '',
      from_date: '',
      to_date: '',
      amount: '',
      percentage: '',
      admin_comment: ''
    });
    setUserAccounts([]);
  };

  const handleDeleteProfit = async (profitId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот доход? Это действие нельзя отменить.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API_URL}/api/admin/profits/${profitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchProfits(currentPage);
    } catch (error) {
      console.error('Error deleting profit:', error);
      setError('Ошибка удаления дохода: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    // Если дата уже в формате DD.MM.YYYY, возвращаем как есть
    if (typeof dateString === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
      return dateString;
    }
    // Иначе форматируем из стандартного формата
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleLogout = () => {
    logoutAdmin();
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Ошибка загрузки пользователей');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserAccounts = async (userId) => {
    if (!userId) {
      setUserAccounts([]);
      return;
    }
    
    try {
      setLoadingAccounts(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      setError('Ошибка загрузки счетов пользователя');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleCreateModalOpen = () => {
    setShowCreateModal(true);
    fetchUsers();
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
    setCreateFormData({
      userId: '',
      account_number: '',
      from_date: '',
      to_date: '',
      amount: '',
      percentage: '',
      admin_comment: ''
    });
    setUserAccounts([]);
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'userId') {
      setCreateFormData(prev => ({
        ...prev,
        [name]: value,
        account_number: ''
      }));
      fetchUserAccounts(value);
    } else {
      setCreateFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCreateProfit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const profitData = {
        userId: parseInt(createFormData.userId),
        account_number: createFormData.account_number,
        from_date: createFormData.from_date,
        to_date: createFormData.to_date,
        amount: parseFloat(createFormData.amount),
        percentage: parseFloat(createFormData.percentage),
        admin_comment: createFormData.admin_comment
      };

      await axios.post(`${API_URL}/api/admin/profits`, profitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      handleCreateModalClose();
      await fetchProfits(currentPage);
    } catch (error) {
      console.error('Error creating profit:', error);
      setError('Ошибка создания дохода: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading__spinner"></div>
        <p>Загрузка доходов...</p>
      </div>
    );
  }

  return (
    <div className="admin-profits">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Доходы</div>
        <div className="admin-header__actions">
          <button 
            className="admin-header__back" 
            onClick={() => navigate('/admin')}
          >
            <span className="--icon-arrow-left"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={handleLogout}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        {error && (
          <div className="admin-error">
            <p>{error}</p>
            <button className="admin-btn admin-btn--primary" onClick={() => setError('')}>
              Закрыть
            </button>
          </div>
        )}

        <div className="admin-profits__filters">
          <div className="admin-filters__top">
            <form onSubmit={handleSearch} className="admin-search-form">
              <input
                type="text"
                placeholder="Поиск по пользователю или номеру счета..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-search-input"
              />
              <button type="submit" className="admin-search-btn">
                <span className="--icon-search"></span>
              </button>
            </form>

            <button 
              className="admin-btn admin-btn--primary admin-btn--create"
              onClick={handleCreateModalOpen}
            >
              <span className="--icon-plus"></span>
              Создать доход
            </button>
          </div>
        </div>

        <div className="admin-profits__table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Пользователь</th>
                <th>Торговый счёт</th>
                <th>Дата с</th>
                <th>Дата по</th>
                <th>Сумма</th>
                <th>Процент</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {profits.map((profit) => (
                <tr key={profit.id}>
                  <td className="admin-table__id">{profit.id}</td>
                  <td className="admin-table__user">
                    <div className="admin-table__user-info">
                      <div className="admin-table__user-name">
                        {profit.first_name} {profit.last_name}
                      </div>
                      <div className="admin-table__user-email">
                        {profit.email}
                      </div>
                    </div>
                  </td>
                  <td className="admin-table__account">
                    {profit.account_number || 'Не указан'}
                  </td>
                  <td className="admin-table__date">
                    {formatDate(profit.from_date)}
                  </td>
                  <td className="admin-table__date">
                    {formatDate(profit.to_date)}
                  </td>
                  <td className="admin-table__amount">
                    {formatCurrency(profit.amount)}
                  </td>
                  <td className="admin-table__percentage">
                    {profit.percentage ? `${profit.percentage}%` : 'Не указан'}
                  </td>
                  <td className="admin-table__actions">
                    <div className="admin-table__action-buttons">
                      <button
                        className="admin-btn admin-btn--primary"
                        onClick={() => handleEditProfit(profit)}
                        style={{ marginBottom: '0.5rem' }}
                      >
                        Редактировать
                      </button>
                      <button
                        className="admin-btn admin-btn--danger"
                        onClick={() => handleDeleteProfit(profit.id)}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {profits.length === 0 && (
            <div className="admin-empty">
              <h2>Доходы не найдены</h2>
              <p>Попробуйте изменить параметры поиска</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination__btn"
              onClick={() => fetchProfits(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Назад
            </button>
            
            <div className="admin-pagination__info">
              Страница {currentPage} из {totalPages}
            </div>
            
            <button
              className="admin-pagination__btn"
              onClick={() => fetchProfits(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Вперед
            </button>
          </div>
        )}
      </div>

      {/* Модальное окно для создания дохода */}
      {showCreateModal && (
        <div className="admin-modal" onClick={handleCreateModalClose}>
          <div className="admin-modal__overlay"></div>
          <div className="admin-modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>Создание дохода</h2>
              <button 
                className="admin-modal__close" 
                onClick={handleCreateModalClose}
              >
                ×
              </button>
            </div>
            
            <div className="admin-modal__body">
              <div className="admin-create-form">
                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Пользователь *</label>
                    <select
                      name="userId"
                      value={createFormData.userId}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__select"
                      required
                      disabled={loadingUsers}
                    >
                      <option value="">Выберите пользователя</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                    {loadingUsers && <div className="admin-loading-text">Загрузка пользователей...</div>}
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Торговый счёт *</label>
                    <select
                      name="account_number"
                      value={createFormData.account_number}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__select"
                      required
                      disabled={!createFormData.userId || loadingAccounts}
                    >
                      <option value="">Выберите торговый счёт</option>
                      {userAccounts.map(account => (
                        <option key={account.id} value={account.account_number}>
                          {account.account_number} ({account.currency}) - {formatCurrency(Number(account.balance))}
                        </option>
                      ))}
                    </select>
                    {loadingAccounts && <div className="admin-loading-text">Загрузка счетов...</div>}
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Дата с *</label>
                    <input
                      type="date"
                      name="from_date"
                      value={createFormData.from_date}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                      required
                    />
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Дата по *</label>
                    <input
                      type="date"
                      name="to_date"
                      value={createFormData.to_date}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                      required
                    />
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Сумма *</label>
                    <input
                      type="number"
                      name="amount"
                      value={createFormData.amount}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Процент *</label>
                    <input
                      type="number"
                      name="percentage"
                      value={createFormData.percentage}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                      step="0.01"
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Примечание</label>
                    <input
                      type="text"
                      name="admin_comment"
                      value={createFormData.admin_comment}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                      placeholder="Примечание администратора"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="admin-modal__footer">
              <div className="admin-create-form__actions">
                <button
                  className="admin-btn admin-btn--secondary"
                  onClick={handleCreateModalClose}
                >
                  Отмена
                </button>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={handleCreateProfit}
                  disabled={!createFormData.userId || !createFormData.account_number || !createFormData.amount || !createFormData.percentage || !createFormData.from_date || !createFormData.to_date}
                >
                  Создать доход
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для редактирования дохода */}
      {editingProfit && (
        <div className="admin-modal" onClick={handleCancelEdit}>
          <div className="admin-modal__overlay"></div>
          <div className="admin-modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>Редактирование дохода #{editingProfit.id}</h2>
              <button 
                className="admin-modal__close" 
                onClick={handleCancelEdit}
              >
                ×
              </button>
            </div>
            
            <div className="admin-modal__body">
              <div className="admin-create-form">
                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Пользователь *</label>
                    <select
                      name="userId"
                      value={editFormData.userId}
                      onChange={handleEditFormChange}
                      className="admin-create-form__select"
                      required
                      disabled={loadingUsers}
                    >
                      <option value="">Выберите пользователя</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                    {loadingUsers && <div className="admin-loading-text">Загрузка пользователей...</div>}
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Торговый счёт *</label>
                    <select
                      name="account_number"
                      value={editFormData.account_number}
                      onChange={handleEditFormChange}
                      className="admin-create-form__select"
                      required
                      disabled={!editFormData.userId || loadingAccounts}
                    >
                      <option value="">Выберите торговый счёт</option>
                      {userAccounts.map(account => (
                        <option key={account.id} value={account.account_number}>
                          {account.account_number} ({account.currency}) - {formatCurrency(Number(account.balance))}
                        </option>
                      ))}
                    </select>
                    {loadingAccounts && <div className="admin-loading-text">Загрузка счетов...</div>}
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Дата с *</label>
                    <input
                      type="date"
                      name="from_date"
                      value={editFormData.from_date}
                      onChange={handleEditFormChange}
                      className="admin-create-form__input"
                      required
                    />
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Дата по *</label>
                    <input
                      type="date"
                      name="to_date"
                      value={editFormData.to_date}
                      onChange={handleEditFormChange}
                      className="admin-create-form__input"
                      required
                    />
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Сумма *</label>
                    <input
                      type="number"
                      name="amount"
                      value={editFormData.amount}
                      onChange={handleEditFormChange}
                      className="admin-create-form__input"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Процент *</label>
                    <input
                      type="number"
                      name="percentage"
                      value={editFormData.percentage}
                      onChange={handleEditFormChange}
                      className="admin-create-form__input"
                      step="0.01"
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Примечание</label>
                    <input
                      type="text"
                      name="admin_comment"
                      value={editFormData.admin_comment}
                      onChange={handleEditFormChange}
                      className="admin-create-form__input"
                      placeholder="Примечание администратора"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="admin-modal__footer">
              <div className="admin-create-form__actions">
                <button
                  className="admin-btn admin-btn--secondary"
                  onClick={handleCancelEdit}
                >
                  Отмена
                </button>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={handleSaveEdit}
                  disabled={!editFormData.userId || !editFormData.account_number || !editFormData.amount || !editFormData.percentage || !editFormData.from_date || !editFormData.to_date}
                >
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfits;

